import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, CalendarCheck, Sparkles, Crown, ConciergeBell, PackageSearch } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageHero, Field } from "@/components/site/ui";
import { useContactEmail } from "@/components/site/ContactLine";

const inputCls = "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40";

export default function Contact({ mode = "contact" }) {
  const isInvite = mode === "invite";
  const email = useContactEmail();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", interest: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(isInvite ? "/request-invitation" : "/contact", f);
      setSent(true); toast.success(data.message || "Sent.");
    } catch (err) { toast.error(apiErr(err)); } finally { setSending(false); }
  };

  return (
    <div>
      <PageHero eyebrow={isInvite ? "By Invitation" : "Get in Touch"}
        title={isInvite ? "Request an Invitation" : "Contact The D"}
        subtitle={isInvite ? "Tell us a little about you. Your host reviews every request personally." : "We'd love to hear from you."} compact />
      <div className="max-w-xl mx-auto px-6 py-16">
        <div className="glass rounded-lg p-6 mb-8 text-center" data-testid="contact-email-card">
          <Mail className="mx-auto text-gold mb-3" size={22} />
          <p className="text-sm text-muted-foreground">For reservations, invitations, private events, membership, or guest assistance, contact The D at</p>
          <a href={`mailto:${email}`} className="font-serif text-xl text-gold hover:underline mt-2 inline-block" data-testid="contact-email-value">{email}</a>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {[["Reservation assistance", CalendarCheck, "Reservation assistance"],
              ["Private-event inquiry", Sparkles, "Private-event inquiry"],
              ["Membership inquiry", Crown, "Membership inquiry"],
              ["Concierge assistance", ConciergeBell, "Concierge assistance"],
              ["Lost-item assistance", PackageSearch, "Lost-item assistance"]].map(([label, Icon, subj]) => (
              <a key={label} href={`mailto:${email}?subject=${encodeURIComponent("The D — " + subj)}`}
                className="text-xs text-pool border border-pool/30 rounded-full px-3 py-1.5 hover:bg-pool/10 inline-flex items-center gap-1.5"
                data-testid={`contact-opt-${label.split(" ")[0].toLowerCase()}`}><Icon size={13} /> {label}</a>
            ))}
          </div>
        </div>
        {sent ? (
          <div className="glass rounded-lg p-10 text-center" data-testid="contact-success">
            <h2 className="font-serif text-3xl">Thank you.</h2>
            <p className="text-muted-foreground mt-3">{isInvite ? "Your request has reached The D. We'll be in touch." : "Your message has been received."}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="glass rounded-lg p-8 space-y-5">
            <Field label="Name" required><input required className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" /></Field>
            <Field label="Email" required><input type="email" required className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" /></Field>
            <Field label="Phone"><input className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            {isInvite && <Field label="What are you interested in?"><input className={inputCls} value={f.interest} onChange={(e) => set("interest", e.target.value)} placeholder="A pool day, a private event, an overnight stay…" /></Field>}
            <Field label="Message" required><textarea required rows={4} className={inputCls} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" /></Field>
            <button disabled={sending} className="btn-gold w-full py-3.5 text-sm flex items-center justify-center gap-2" data-testid="contact-submit">
              {sending ? <Loader2 className="animate-spin" size={16} /> : null} {isInvite ? "Request an Invitation" : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
